import { toast } from "react-toastify";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

// The finalize-configuration gate only applies to Live trials; UAT trials are never locked or hidden by it
export const isTrialConfigFinalized = (trial) =>
  trial?.environment === "Live" && !!trial?.is_finalized;

export const isTrialPendingFinalization = (trial) =>
  trial?.environment === "Live" && !trial?.is_finalized;

export const idsParam = (items, key = "id") => (items || []).map((item) => item[key]).join(",");

export const showApiErrorToast = (error, fallbackMessage = "Something went wrong") => {
  if (!error?.response?.data) {
    toast.error(fallbackMessage);
    return;
  }

  const data = error.response.data;

  // DRF serializer errors: { field: ["error message"] }
  if (typeof data === "object") {
    const firstKey = Object.keys(data)[0];
    const firstError = data[firstKey];

    if (Array.isArray(firstError)) {
      toast.error(firstError[0]);
      return;
    }

    if (typeof firstError === "string") {
      toast.error(firstError);
      return;
    }
  }

  toast.error(fallbackMessage);
};

export const downloadSignedFile = async (url, filename = "document") => {
  try {
    const response = await fetch(url, {
      method: "GET",
      mode: "cors",
    });

    if (!response.ok) {
      throw new Error("Failed to download file");
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error(error);
    alert("Unable to download document");
  }
};

export const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return null;
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

// Fractional hours as a readable duration, rounded to whole minutes like the backend (1.5 -> "1h 30m")
export const formatHoursDuration = (hours) => {
  if (hours === null || hours === undefined || hours === "") return "-";
  const totalMinutes = Math.round(Number(hours) * 60);
  if (Number.isNaN(totalMinutes)) return "-";
  const absoluteMinutes = Math.abs(totalMinutes);
  const wholeHours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;
  const parts = [wholeHours && `${wholeHours}h`, minutes && `${minutes}m`].filter(Boolean);
  if (!parts.length) return "0h";
  return `${totalMinutes < 0 ? "-" : ""}${parts.join(" ")}`;
};

// Parses an hours input allowing up to 2 decimals; returns null when invalid or not above the minimum
export const parseHoursInput = (value, { allowZeroOrNegative = false } = {}) => {
  const text = `${value ?? ""}`.trim();
  const pattern = allowZeroOrNegative
    ? /^-?(\d+(\.\d{1,2})?|\.\d{1,2})$/
    : /^(\d+(\.\d{1,2})?|\.\d{1,2})$/;
  if (!pattern.test(text)) return null;
  const hours = Number(text);
  if (!allowZeroOrNegative && hours <= 0) return null;
  return hours;
};

export const formatDate = (date, format = "MM/DD/YYYY, hh:mm:ss A") => {
  if (!date) return "-";
  const d = dayjs.utc(date);
  if (!d.isValid()) return "-";
  return d.local().format(format);
};

export const toISOString = (val) => {
  if (!val) return null;
  const d = dayjs.utc(val);
  return d.isValid() ? d.toISOString() : null;
};
